import { useState, useCallback } from "react";
import { useAccount, useSwitchChain, useSignTypedData } from "wagmi";
import * as api from "@/lib/api";
import {
  parsePaymentRequired,
  buildEIP712TypedData,
  encodePaymentSignature,
  chainIdFromNetwork,
  type PaymentAccept,
} from "@/lib/x402";
import type { Address, Hex } from "viem";

export type PaymentState =
  | { step: "idle" }
  | { step: "loading" }
  | { step: "paid"; contentMarkdown: string }
  | { step: "paywall"; accepts: PaymentAccept[]; paymentRequired: string }
  | { step: "switching-chain" }
  | { step: "signing" }
  | { step: "submitting" }
  | { step: "error"; message: string };

const PAID_STORAGE_KEY = "x402stack_paid";

function getPaidMap(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(PAID_STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

function markPaid(articleId: string, payerAddress: string) {
  const map = getPaidMap();
  map[articleId] = payerAddress;
  localStorage.setItem(PAID_STORAGE_KEY, JSON.stringify(map));
}

export function useArticleContent(articleId: string) {
  const [state, setState] = useState<PaymentState>({ step: "idle" });
  const { address, chainId } = useAccount();
  const { switchChainAsync } = useSwitchChain();
  const { signTypedDataAsync } = useSignTypedData();

  const fetchContent = useCallback(async () => {
    setState({ step: "loading" });

    // Check localStorage for re-read, then connected wallet
    const paidMap = getPaidMap();
    const storedPayer = paidMap[articleId];
    const payerAddress = storedPayer || address;

    try {
      const res = await api.getArticleContent(
        articleId,
        payerAddress || undefined,
      );
      if (res.status === "ok" && res.content) {
        setState({ step: "paid", contentMarkdown: res.content.contentMarkdown });
        return;
      }
      if (res.status === "payment-required" && res.paymentRequired) {
        const parsed = parsePaymentRequired(res.paymentRequired);
        setState({ step: "paywall", accepts: parsed.accepts, paymentRequired: res.paymentRequired });
        return;
      }
      setState({ step: "paywall", accepts: [], paymentRequired: "" });
    } catch (err) {
      setState({
        step: "error",
        message: err instanceof Error ? err.message : "Failed to load content",
      });
    }
  }, [articleId, address]);

  const pay = useCallback(
    async (accept: PaymentAccept) => {
      if (!address) {
        setState({ step: "error", message: "Connect your wallet first" });
        return;
      }

      try {
        // Switch chain if needed
        const requiredChainId = chainIdFromNetwork(accept.network);
        if (chainId !== requiredChainId) {
          setState({ step: "switching-chain" });
          await switchChainAsync({ chainId: requiredChainId });
        }

        // Build and sign EIP-712 typed data
        setState({ step: "signing" });
        const typedData = buildEIP712TypedData(accept, address);
        const signature = await signTypedDataAsync({
          domain: typedData.domain,
          types: typedData.types,
          primaryType: typedData.primaryType,
          message: typedData.message,
        });

        // Submit payment
        setState({ step: "submitting" });
        const paymentHeader = encodePaymentSignature(
          accept,
          signature as Hex,
          address as Address,
          typedData,
        );

        const res = await api.getArticleContent(
          articleId,
          address,
          paymentHeader,
        );

        if (res.status === "ok" && res.content) {
          markPaid(articleId, address);
          setState({
            step: "paid",
            contentMarkdown: res.content.contentMarkdown,
          });
        } else {
          setState({
            step: "error",
            message: "Payment submitted but content not returned",
          });
        }
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Payment failed";
        // User rejected = go back to paywall
        if (message.includes("rejected") || message.includes("denied")) {
          setState({ step: "paywall", accepts: [accept], paymentRequired: "" });
        } else {
          setState({ step: "error", message });
        }
      }
    },
    [address, chainId, switchChainAsync, signTypedDataAsync, articleId],
  );

  return { state, fetchContent, pay };
}
